import React, { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconDownload,
  IconEye,
  IconLock,
  IconMail,
  IconPencil,
  IconPlus,
  IconSignature,
  IconTrash,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useClinicalNotes } from '../../entities/ClinicalNote/queries/useClinicalNotes';
import {
  useDeleteClinicalNote,
  useDownloadClinicalNotesPdf,
  useSendClinicalNotesToAssistant,
  useSignCounsellingNote,
} from '../../entities/ClinicalNote/mutations';
import {
  ClinicalNote,
  ClinicalNoteType,
} from '../../entities/ClinicalNote/ClinicalNote';
import { ClinicalNoteActivityHistory } from './ClinicalNoteActivityHistory';
import { TableContextProvider, Th, useTable } from '../../hooks/useTable';
import tableTools from '../../helpers/tableTools';
import HcpOnlyCaption from '../molecules/HcpOnlyCaption/HcpOnlyCaption';

/**
 * Per-feature configuration injected by the thin per-type wrapper components.
 * `editRouteSegment` is the relative URL segment used when navigating to the
 * edit/create form (e.g. `physicalExamination/0` to create, `${segment}/${id}` to edit).
 */
export type ClinicalNoteSectionConfig = {
  noteType: ClinicalNoteType;
  i18nPrefix: string;
  editRouteSegment: string;
  /** Hide the Send-to-Assistant button when a note type does not yet have an email template. */
  showSendToAssistant?: boolean;
  /** Counselling notes (AMI-389) expose Sign and per-row lock/ack badges. */
  supportsDualAck?: boolean;
};

interface ClinicalNoteSectionProps {
  config: ClinicalNoteSectionConfig;
  patientId?: string | number;
  patientName?: string | null;
  patientDateOfBirth?: string | null;
}

const ClinicalNoteSectionInner: React.FC<ClinicalNoteSectionProps> = ({
  config,
  patientId,
  patientName,
  patientDateOfBirth,
}) => {
  const {
    noteType,
    i18nPrefix,
    editRouteSegment,
    showSendToAssistant = true,
    supportsDualAck = false,
  } = config;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const resolvedPatientId = patientId != null ? String(patientId) : undefined;
  const { data, isLoading } = useClinicalNotes(noteType, resolvedPatientId);

  const remove = useDeleteClinicalNote(noteType, i18nPrefix, resolvedPatientId);
  const downloadPdf = useDownloadClinicalNotesPdf(noteType, i18nPrefix);
  const sendToAssistant = useSendClinicalNotesToAssistant(noteType, i18nPrefix);
  const sign = useSignCounsellingNote(resolvedPatientId);

  const [includeActivityLog, setIncludeActivityLog] = useState<boolean>(false);

  const {
    sortedData,
    setSortedData,
    sortBy,
    setSortBy,
    reverseSortDirection,
    setReverseSortDirection,
    setSorting,
  } = useTable();

  useEffect(() => {
    if (!data?.clinicalNotes) return;
    setSortedData(
      tableTools.sortData([...data.clinicalNotes], {
        sortBy: 'lastModifiedDate',
        reversed: true,
        search: '',
      }),
    );
    setSortBy('lastModifiedDate');
    setReverseSortDirection(true);
  }, [data, setSortedData, setSortBy, setReverseSortDirection]);

  const notes = sortedData;

  const pad = (n: number) => String(n).padStart(2, '0');

  // Exam date is user-entered local time — format using local components.
  const formatExamDate = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

  // Backend serializes LastModifiedDate in UTC; display the stored value as-is
  // rather than letting timezone conversion shift it to the browser's timezone.
  const formatLastModified = (d: Date | null) => {
    if (!d) return '';
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  };

  const handleDelete = (note: ClinicalNote) => {
    modals.openConfirmModal({
      title: t(
        `${i18nPrefix}.delete.title`,
        t(`${i18nPrefix}.rowActions.delete`),
      ),
      children: (
        <Stack gap="sm">
          <Text>{t(`${i18nPrefix}.delete.confirm`)}</Text>
          <Text size="sm" c="dimmed">
            {formatExamDate(note.examDate)}
            {note.questionnaireName ? ` — ${note.questionnaireName}` : ''}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
      confirmProps: { color: 'red' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: () => remove.mutate(String(note.id)),
    });
  };

  const handleSign = (note: ClinicalNote) => {
    modals.openConfirmModal({
      title: t(`${i18nPrefix}.sign.title`, t(`${i18nPrefix}.rowActions.sign`)),
      children: (
        <Stack gap="sm">
          <Text>{t(`${i18nPrefix}.sign.confirm`)}</Text>
          <Text size="sm" c="dimmed">
            {formatExamDate(note.examDate)}
            {patientName ? ` — ${patientName}` : ''}
          </Text>
        </Stack>
      ),
      labels: {
        confirm: t(`${i18nPrefix}.rowActions.sign`),
        cancel: t('button.cancel'),
      },
      confirmProps: { color: 'blue' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: () => sign.mutate(String(note.id)),
    });
  };

  const counsellingStatusLabel = (
    note: ClinicalNote,
  ): { label: string; color: string } | null => {
    if (!supportsDualAck) return null;
    if (note.isLocked)
      return { label: t(`${i18nPrefix}.status.locked`), color: 'green' };
    if (note.hcpSigned) {
      return {
        label: t(`${i18nPrefix}.status.awaitingPatient`),
        color: 'yellow',
      };
    }
    if (note.patientAcknowledged)
      return { label: t(`${i18nPrefix}.status.awaitingHcp`), color: 'orange' };
    return { label: t(`${i18nPrefix}.status.draft`), color: 'gray' };
  };

  const handleDownloadPdf = () => {
    if (!resolvedPatientId) return;
    downloadPdf.mutate({
      patientId: resolvedPatientId,
      language: i18n.language,
      includeActivityLog,
      patientName: patientName ?? null,
      patientDateOfBirth: patientDateOfBirth ?? null,
    });
  };

  const handleSendToAssistant = () => {
    if (!resolvedPatientId) return;
    sendToAssistant.mutate({
      patientId: resolvedPatientId,
      language: i18n.language,
    });
  };

  return (
    <Paper p="md" radius={0}>
      <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="sm">
        <HcpOnlyCaption />
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate(`../${editRouteSegment}/0`)}
        >
          {t(`${i18nPrefix}.toolbar.add`)}
        </Button>
      </Flex>

      {isLoading ? (
        <Loader size="sm" />
      ) : notes.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t(`${i18nPrefix}.empty`)}
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Th
                sorted={sortBy === 'examDate'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('examDate', sortedData)}
              >
                {t(`${i18nPrefix}.columns.examDate`)}
              </Th>
              <Th
                sorted={sortBy === 'lastModifiedDate'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('lastModifiedDate', sortedData)}
              >
                {t(`${i18nPrefix}.columns.lastModifiedDate`)}
              </Th>
              <Th
                sorted={sortBy === 'questionnaireName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('questionnaireName', sortedData)}
              >
                {t(`${i18nPrefix}.columns.questionnaire`)}
              </Th>
              <Th
                sorted={sortBy === 'patientTaskCompleted'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('patientTaskCompleted', sortedData)}
              >
                {t(`${i18nPrefix}.columns.patientTask`)}
              </Th>
              {supportsDualAck && (
                <Th
                  sorted={sortBy === 'isLocked'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('isLocked', sortedData)}
                >
                  {t(`${i18nPrefix}.columns.status`)}
                </Th>
              )}
              <Table.Th style={{ width: 140 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {notes.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>{formatExamDate(e.examDate)}</Table.Td>
                <Table.Td>{formatLastModified(e.lastModifiedDate)}</Table.Td>
                <Table.Td>
                  {e.questionnaireName ?? (
                    <Text size="sm" c="dimmed">
                      {t(`${i18nPrefix}.freeTextOnly`)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {e.hasPreVisitQuestionnaire ? (
                    <Badge
                      color={e.patientTaskCompleted ? 'green' : 'yellow'}
                      variant="light"
                    >
                      {e.patientTaskCompleted
                        ? t(`${i18nPrefix}.preVisit.completed`)
                        : t(`${i18nPrefix}.preVisit.pending`)}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                {supportsDualAck && (
                  <Table.Td>
                    {(() => {
                      const status = counsellingStatusLabel(e);
                      return status ? (
                        <Badge
                          color={status.color}
                          variant="light"
                          leftSection={
                            e.isLocked ? <IconLock size={12} /> : null
                          }
                        >
                          {status.label}
                        </Badge>
                      ) : null;
                    })()}
                  </Table.Td>
                )}
                <Table.Td>
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    {supportsDualAck && !e.isLocked && !e.hcpSigned && (
                      <Tooltip
                        label={t(`${i18nPrefix}.rowActions.sign`)}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          aria-label={t(`${i18nPrefix}.rowActions.sign`)}
                          onClick={() => handleSign(e)}
                          loading={sign.isPending}
                        >
                          <IconSignature size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {supportsDualAck && e.isLocked ? (
                      <Tooltip
                        label={t(`${i18nPrefix}.rowActions.consult`)}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          aria-label={t(`${i18nPrefix}.rowActions.consult`)}
                          onClick={() =>
                            navigate(`../${editRouteSegment}/${e.id}`)
                          }
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        label={t(`${i18nPrefix}.rowActions.edit`)}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          aria-label={t(`${i18nPrefix}.rowActions.edit`)}
                          disabled={e.isLocked}
                          onClick={() =>
                            navigate(`../${editRouteSegment}/${e.id}`)
                          }
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip
                      label={t(`${i18nPrefix}.rowActions.delete`)}
                      withArrow
                    >
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t(`${i18nPrefix}.rowActions.delete`)}
                        disabled={e.isLocked}
                        onClick={() => handleDelete(e)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {notes.length > 0 && (
        <Flex justify="flex-end" gap="sm" wrap="wrap" mt="md" align="center">
          <Checkbox
            label={t(`${i18nPrefix}.toolbar.includeActivityLog`)}
            checked={includeActivityLog}
            onChange={(event) =>
              setIncludeActivityLog(event.currentTarget.checked)
            }
          />
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadPdf}
            loading={downloadPdf.isPending}
          >
            {t(`${i18nPrefix}.toolbar.downloadPdf`)}
          </Button>
          {showSendToAssistant && (
            <Button
              variant="light"
              color="grape"
              leftSection={<IconMail size={16} />}
              onClick={handleSendToAssistant}
              loading={sendToAssistant.isPending}
            >
              {t(`${i18nPrefix}.toolbar.sendToAssistant`)}
            </Button>
          )}
        </Flex>
      )}

      <Stack mt="lg" gap="sm">
        <ClinicalNoteActivityHistory
          noteType={noteType}
          i18nPrefix={i18nPrefix}
          patientId={resolvedPatientId}
        />
      </Stack>
    </Paper>
  );
};

const ClinicalNoteSection: React.FC<ClinicalNoteSectionProps> = (props) => (
  <TableContextProvider>
    <ClinicalNoteSectionInner {...props} />
  </TableContextProvider>
);

export default ClinicalNoteSection;
